import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, FiEdit, FiTrash2, FiEye, FiUsers, FiBarChart2, 
  FiVideo, FiFileText, FiImage, FiPlus, FiPlay, FiPause
} from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { useTheme } from '../../contexts/ThemeContext';
import { teacherCourseAPI } from '../../api/teacherEndpoints';
import { 
  DashboardContainer, 
  DashboardHeader, 
  StatsGrid, 
  StatCard, 
  StatIcon, 
  StatInfo, 
  SectionTitle,
  ContentGrid,
  ContentCard,
  CourseThumbnail,
  ContentCardHeader,
  ContentCardBody,
} from '../dashboards/Dashboard.styles';

const TeacherCourseManagement = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const menuItems = [
    { icon: <FiArrowLeft />, label: 'Back to Dashboard', path: '/teacher' },
    { icon: <FiEdit />, label: 'Edit Course', path: `/teacher/courses/${courseId}/edit` },
    { icon: <FiUsers />, label: 'Students', path: `/teacher/courses/${courseId}/students` },
  ];

  useEffect(() => {
    fetchCourseDetails();
  }, [courseId]);

  const fetchCourseDetails = async () => {
    try {
      setLoading(true);
      const response = await teacherCourseAPI.getCourse(courseId);
      setCourse(response.data.course_content || response.data.course);
    } catch (error) {
      console.error('Failed to load course details:', error);
      showToast('error', 'Failed to load course details');
      navigate('/teacher');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async () => {
    try {
      setDeleting(true);
      await teacherCourseAPI.deleteCourse(courseId);
      showToast('success', 'Course deleted successfully');
      navigate('/teacher');
    } catch (error) {
      showToast('error', 'Failed to delete course');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handlePublishToggle = async () => {
    try {
      if (course.is_published) {
        await teacherCourseAPI.unpublishCourse(courseId);
        showToast('success', 'Course unpublished successfully');
      } else {
        await teacherCourseAPI.publishCourse(courseId);
        showToast('success', 'Course published successfully');
      }
      fetchCourseDetails(); // Refresh course data
    } catch (error) {
      showToast('error', 'Failed to update course status');
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading course details..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>Course not found</h2>
            <p>The course you're looking for doesn't exist or you don't have permission to view it.</p>
            <Button onClick={() => navigate('/teacher')}>
              Back to Dashboard
            </Button>
          </div>
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>{course.title}</h1>
            <p>{course.description}</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button 
              variant="ghost" 
              leftIcon={course.is_published ? <FiPause /> : <FiPlay />}
              onClick={handlePublishToggle}
            >
              {course.is_published ? 'Unpublish' : 'Publish'}
            </Button>
            <Button 
              leftIcon={<FiEdit />}
              onClick={() => navigate(`/teacher/courses/${courseId}/edit`)}
            >
              Edit Course
            </Button>
            <Button 
              variant="ghost" 
              leftIcon={<FiTrash2 />}
              onClick={() => setShowDeleteModal(true)}
              style={{ color: '#EF4444' }}
            >
              Delete
            </Button>
          </div>
        </DashboardHeader>

        {/* Course Statistics */}
        <StatsGrid>
          <StatCard>
            <StatIcon color="#2c5f34">
              <FiUsers />
            </StatIcon>
            <StatInfo>
              <h3>{course.enrolled_students || 0}</h3>
              <p>Enrolled Students</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#10B981">
              <FiBarChart2 />
            </StatIcon>
            <StatInfo>
              <h3>{course.completion_rate || 0}%</h3>
              <p>Completion Rate</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#F59E0B">
              <FiVideo />
            </StatIcon>
            <StatInfo>
              <h3>{course.videos?.length || 0}</h3>
              <p>Videos</p>
            </StatInfo>
          </StatCard>

          <StatCard>
            <StatIcon color="#8B5CF6">
              <FiFileText />
            </StatIcon>
            <StatInfo>
              <h3>{course.files?.length || 0}</h3>
              <p>Files</p>
            </StatInfo>
          </StatCard>
        </StatsGrid>

        {/* Course Content */}
        <SectionTitle>Course Content</SectionTitle>
        
        {course.videos && course.videos.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>Videos ({course.videos.length})</h3>
            <ContentGrid>
              {course.videos.map((video) => (
                <ContentCard key={video.id}>
                  <CourseThumbnail>
                    {video.thumbnail_url ? (
                      <img src={video.thumbnail_url} alt={video.title} />
                    ) : (
                      <div className="no-thumbnail">
                        <FiVideo />
                        <span>Video Thumbnail</span>
                      </div>
                    )}
                  </CourseThumbnail>
                  <ContentCardHeader>
                    <h3>{video.title}</h3>
                  </ContentCardHeader>
                  <ContentCardBody>
                    <p>{video.description || 'No description available'}</p>
                    <div className="meta">
                      <span>Duration: {Math.floor(video.duration / 60)} minutes</span>
                      <span>Type: {video.file_type}</span>
                    </div>
                  </ContentCardBody>
                </ContentCard>
              ))}
            </ContentGrid>
          </div>
        )}

        {course.files && course.files.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ marginBottom: '16px' }}>Files ({course.files.length})</h3>
            <ContentGrid>
              {course.files.map((file) => (
                <ContentCard key={file.id}>
                  <CourseThumbnail>
                    <div className="no-thumbnail">
                      <FiFileText />
                      <span>File</span>
                    </div>
                  </CourseThumbnail>
                  <ContentCardHeader>
                    <h3>{file.title}</h3>
                  </ContentCardHeader>
                  <ContentCardBody>
                    <p>{file.description || 'No description available'}</p>
                    <div className="meta">
                      <span>Type: {file.file_type}</span>
                      <span>Size: {(file.file_size / 1024 / 1024).toFixed(1)} MB</span>
                    </div>
                  </ContentCardBody>
                </ContentCard>
              ))}
            </ContentGrid>
          </div>
        )}

        {(!course.videos || course.videos.length === 0) && (!course.files || course.files.length === 0) && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <FiVideo size={48} style={{ color: theme.colors.textTertiary, marginBottom: '16px' }} />
            <h3>No content yet</h3>
            <p>Add videos and files to your course to get started.</p>
            <Button leftIcon={<FiPlus />} onClick={() => navigate(`/teacher/courses/${courseId}/edit`)}>
              Add Content
            </Button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Course"
          size="small"
        >
          <div style={{ padding: '20px 0' }}>
            <p>Are you sure you want to delete "{course.title}"? This action cannot be undone.</p>
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              marginTop: '20px'
            }}>
              <Button 
                variant="ghost" 
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteCourse}
                loading={deleting}
                style={{ background: '#EF4444' }}
              >
                Delete Course
              </Button>
            </div>
          </div>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default TeacherCourseManagement;
